import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const DEFAULT_VARIANT_TITLE = 'Default Title';

const desktopMediaQuery = window.matchMedia('(min-width: 1024px)');
const SKIP_CART_NOTIFICATION_FLAG = 'skipCartNotification';

function shouldSkipCartNotification() {
  return document.documentElement.dataset[SKIP_CART_NOTIFICATION_FLAG] === 'true';
}

function clearSkipCartNotification() {
  delete document.documentElement.dataset[SKIP_CART_NOTIFICATION_FLAG];
}

/**
 * @typedef {Object} CartNotificationState
 * @property {string} heading
 * @property {string} image
 * @property {string} imageAlt
 * @property {string} productTitle
 * @property {string} url
 * @property {string[]} details
 * @property {string} price
 * @property {number} itemCount
 * @property {number} itemQuantity
 */

/**
 * @typedef {Object} CartNotificationOption
 * @property {string} name
 * @property {string} value
 */

/**
 * @typedef {Object} CartNotificationImage
 * @property {string} [url]
 * @property {string} [src]
 * @property {string} [alt]
 */

/**
 * @typedef {Object} CartNotificationItem
 * @property {string} [key]
 * @property {string | number} [variant_id]
 * @property {CartNotificationOption[]} [options_with_values]
 * @property {string} [variant_title]
 * @property {Record<string, unknown>} [properties]
 * @property {{ selling_plan?: { name?: string } }} [selling_plan_allocation]
 * @property {string} [image]
 * @property {CartNotificationImage} [featured_image]
 * @property {string} [product_title]
 * @property {string} [title]
 * @property {string} [url]
 * @property {number} [quantity]
 * @property {number} [final_price]
 * @property {number} [price]
 * @property {number} [original_price]
 * @property {number} [final_line_price]
 * @property {number} [line_price]
 */

class CartNotification extends DialogComponent {
  /** @type {HTMLButtonElement[]} */
  #dismissButtons = [];

  /** @type {MutationObserver | null} */
  #payButtonObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#dismissButtons = Array.from(
      this.querySelectorAll('[data-cart-notification-dismiss]')
    ).filter((button) => button instanceof HTMLButtonElement);

    document.addEventListener(ThemeEvents.cartUpdate, /** @type {EventListener} */ (this.#handleCartUpdate));
    document.addEventListener(ThemeEvents.cartError, /** @type {EventListener} */ (this.#handleCartError));

    window.addEventListener('resize', this.#syncNotificationState);

    this.#observePayButtonSource();
    this.#syncPayButton();
    this.#syncNotificationState();

    for (const button of this.#dismissButtons) {
      button.addEventListener('click', this.closeDialog);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      /** @type {EventListener} */ (this.#handleCartUpdate)
    );
    document.removeEventListener(
      ThemeEvents.cartError,
      /** @type {EventListener} */ (this.#handleCartError)
    );
    window.removeEventListener('resize', this.#syncNotificationState);
    this.#payButtonObserver?.disconnect();
    this.#payButtonObserver = null;

    for (const button of this.#dismissButtons) {
      button.removeEventListener('click', this.closeDialog);
    }
  }

  /**
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    const source = event.detail?.data?.source;

    if (event.detail?.data?.didError) return;

    if (source === 'product-form-component') {
      if (shouldSkipCartNotification()) {
        clearSkipCartNotification();
        return;
      }

      const state = this.#buildState(event);

      if (!state) return;

      this.#render(state, this.#getRenderedProductMarkup(event));
      this.#syncPayButton();

      if (desktopMediaQuery.matches) {
        this.closeDialog();
        this.#openCartDrawer();
        return;
      }

      this.showDialog();
      return;
    }

    if (source === 'cart-items-component' || event.detail?.resource?.item_count === 0) {
      this.closeDialog();
    }
  };

  /**
   * @param {CustomEvent} event
   */
  #handleCartError = (event) => {
    if (!this.#isCurrentProductError(event)) return;

    const state = this.#buildErrorState(event);
    if (!state) return;

    this.#renderError(state);
    this.#hidePayButton();
    this.showDialog();
  };

  #openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer instanceof HTMLElement && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  }

  /**
   * @param {CustomEvent} event
   * @returns {CartNotificationState | null}
   */
  #buildState(event) {
    /** @type {CartNotificationItem[]} */
    const cartItems = Array.isArray(event.detail?.resource?.items) ? event.detail.resource.items : [];
    const primaryKey = event.detail?.data?.lineItemKey || '';
    const explicitKeys = primaryKey ? [primaryKey] : [];

    if (Array.isArray(event.detail?.data?.lineItemKeys)) {
      explicitKeys.push(...event.detail.data.lineItemKeys.filter(Boolean));
    }

    let item = primaryKey ? cartItems.find((cartItem) => cartItem.key === primaryKey) : null;

    if (!item && explicitKeys.length) {
      item = cartItems.find((cartItem) => explicitKeys.includes(cartItem.key));
    }

    if (!item) {
      const sourceId = event.detail?.sourceId != null ? String(event.detail.sourceId) : '';
      const matchingItems = cartItems.filter((cartItem) => String(cartItem.variant_id) === sourceId);
      item = matchingItems[matchingItems.length - 1] ?? null;
    }

    if (!item) return null;

    const details = [];
    const options = Array.isArray(item.options_with_values) ? item.options_with_values : [];

    if (options.length > 0) {
      details.push(...options.map((option) => `${option.name}: ${option.value}`));
    } else if (item.variant_title && item.variant_title !== DEFAULT_VARIANT_TITLE) {
      details.push(item.variant_title);
    }

    details.push(...this.#extractProperties(item.properties));

    if (item.selling_plan_allocation?.selling_plan?.name) {
      details.push(item.selling_plan_allocation.selling_plan.name);
    }

    return {
      heading: this.#buildHeading(Number(event.detail?.data?.itemCount) || 1),
      image: item.image || item.featured_image?.url || item.featured_image?.src || '',
      imageAlt: item.featured_image?.alt || item.product_title || item.title || '',
      productTitle: item.product_title || item.title || '',
      url: item.url || '',
      details,
      price: this.#formatPrice(item, Number(event.detail?.data?.itemCount) || 1),
      itemCount: Number(event.detail?.resource?.item_count) || 0,
      itemQuantity: Number(item.quantity) || 0,
    };
  }

  /**
   * @param {number} itemCount
   * @returns {string}
   */
  #buildHeading(itemCount) {
    const oneItemText = this.dataset.itemsAddedOne || '1 item added to cart';
    const multipleItemsText = this.dataset.itemsAddedOther || '{{ count }} items added to cart';

    if (itemCount === 1) return oneItemText;

    return multipleItemsText.replace('{{ count }}', String(itemCount));
  }

  /**
   * @param {unknown} properties
   * @returns {string[]}
   */
  #extractProperties(properties) {
    if (!properties || typeof properties !== 'object') return [];

    return Object.entries(properties)
      .filter(([key, value]) => value != null && value !== '' && !String(key).startsWith('_'))
      .map(([key, value]) => {
        const normalizedValue = String(value).includes('/uploads/')
          ? String(value).split('/').pop() || String(value)
          : String(value);

        return `${key}: ${normalizedValue}`;
      });
  }

  /**
   * @param {CartNotificationState} state
   * @param {string} [renderedProductMarkup]
   */
  #render(state, renderedProductMarkup = '') {
    const heading = this.querySelector('[data-cart-notification-heading]');
    const productContainer = this.querySelector('[data-cart-notification-product]');

    if (!(heading instanceof HTMLElement) || !(productContainer instanceof HTMLElement)) return;

    this.dataset.state = 'success';
    this.#setIconState('success');

    heading.textContent = state.heading;
    productContainer.innerHTML = renderedProductMarkup || this.#renderProduct(state);
    this.#syncNotificationState();
  }

  /**
   * @param {CustomEvent} event
   * @returns {string}
   */
  #getRenderedProductMarkup(event) {
    const sections = event.detail?.data?.sections;
    const lineItemKeys = this.#getLineItemKeys(event);

    if (!sections || !lineItemKeys.length) {
      return '';
    }

    for (const html of Object.values(sections)) {
      if (typeof html !== 'string') continue;

      const sectionDocument = new DOMParser().parseFromString(html, 'text/html');

      for (const key of lineItemKeys) {
        const template = sectionDocument.querySelector(
          `template[data-cart-notification-product-template][data-key="${CSS.escape(key)}"]`
        );

        if (template instanceof HTMLTemplateElement) {
          return template.innerHTML.trim();
        }
      }
    }

    return '';
  }

  /**
   * @param {CustomEvent} event
   * @returns {string[]}
   */
  #getLineItemKeys(event) {
    const primaryKey = event.detail?.data?.lineItemKey;
    const keys = [];

    if (typeof primaryKey === 'string' && primaryKey) {
      keys.push(primaryKey);
    }

    if (Array.isArray(event.detail?.data?.lineItemKeys)) {
      keys.push(...event.detail.data.lineItemKeys.filter((key) => typeof key === 'string' && key));
    }

    return [...new Set(keys)];
  }

  #syncNotificationState = () => {
    const heading = this.querySelector('[data-cart-notification-heading]');
    if (!(heading instanceof HTMLElement)) return;

    const isDesktop = desktopMediaQuery.matches;
    const isError = this.dataset.state === 'error';

    heading.hidden = isDesktop && !isError;
    this.#setIconState(isError ? 'error' : 'success');
  };

  /**
   * @param {{ heading: string, message: string }} state
   */
  #renderError(state) {
    const heading = this.querySelector('[data-cart-notification-heading]');
    const productContainer = this.querySelector('[data-cart-notification-product]');

    if (!(heading instanceof HTMLElement) || !(productContainer instanceof HTMLElement)) return;

    this.dataset.state = 'error';
    heading.textContent = state.heading;
    productContainer.innerHTML = this.#renderErrorMessage(state.message);
    this.#syncNotificationState();
  }

  /**
   * @param {'success' | 'error'} state
   */
  #setIconState(state) {
    const successIcon = this.querySelector('[data-cart-notification-icon-success]');
    const errorIcon = this.querySelector('[data-cart-notification-icon-error]');

    if (!(successIcon instanceof HTMLElement) || !(errorIcon instanceof HTMLElement)) return;

    if (desktopMediaQuery.matches) {
      successIcon.hidden = true;
      errorIcon.hidden = state !== 'error';
      return;
    }

    successIcon.hidden = state !== 'success';
    errorIcon.hidden = state !== 'error';
  }

  #observePayButtonSource() {
    const stickyAddToCart = document.querySelector('sticky-add-to-cart');
    if (!(stickyAddToCart instanceof HTMLElement)) return;

    this.#payButtonObserver = new MutationObserver(() => {
      this.#syncPayButton();
    });

    this.#payButtonObserver.observe(stickyAddToCart, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'hidden', 'style'],
    });
  }

  #syncPayButton() {
    const payButtonContainer = this.querySelector('[data-cart-notification-pay-button]');
    if (!(payButtonContainer instanceof HTMLElement)) return;

    const sourceButton = document.querySelector(
      'sticky-add-to-cart .sticky-add-to-cart__button--pay.button--apple-pay'
    );

    if (!(sourceButton instanceof HTMLButtonElement) || sourceButton.disabled) {
      payButtonContainer.hidden = true;
      payButtonContainer.replaceChildren();
      return;
    }

    const clonedButton = /** @type {HTMLButtonElement} */ (sourceButton.cloneNode(true));
    clonedButton.removeAttribute('ref');
    clonedButton.removeAttribute('on:click');
    clonedButton.type = 'button';
    clonedButton.disabled = false;
    clonedButton.addEventListener('click', this.#handlePayButtonClick);

    payButtonContainer.replaceChildren(clonedButton);
    payButtonContainer.hidden = false;
  }

  #hidePayButton() {
    const payButtonContainer = this.querySelector('[data-cart-notification-pay-button]');
    if (!(payButtonContainer instanceof HTMLElement)) return;

    payButtonContainer.hidden = true;
    payButtonContainer.replaceChildren();
  }

  #handlePayButtonClick = () => {
    window.location.href = '/checkout?payment=shop_pay';
  };

  /**
   * @param {CustomEvent} event
   * @returns {boolean}
   */
  #isCurrentProductError(event) {
    const currentProductId = this.dataset.productId;
    if (!currentProductId) return true;

    const eventTarget = event.target;
    if (!(eventTarget instanceof HTMLElement)) return false;

    const productForm = eventTarget.closest('product-form-component');
    if (!(productForm instanceof HTMLElement)) return false;

    return productForm.dataset.productId === currentProductId;
  }

  /**
   * @param {CustomEvent} event
   * @returns {{ heading: string, message: string } | null}
   */
  #buildErrorState(event) {
    const message = this.#normalizeText(event.detail?.data?.message);
    const description = this.#normalizeText(event.detail?.data?.description);
    const heading = message || description;

    if (!heading) return null;

    return {
      heading,
      message: description && description !== heading ? description : '',
    };
  }

  /**
   * @param {CartNotificationState} state
   * @returns {string}
   */
  #renderProduct(state) {
    const productTitle = this.#escapeHtml(state.productTitle);
    const productUrl = state.url ? this.#escapeHtml(state.url) : '';
    const priceMarkup = state.price
      ? `<p class="cart-notification-product__price">${this.#escapeHtml(state.price)}</p>`
      : '';

    const detailMarkup = state.details
      .map((detail) => {
        const [rawLabel, ...rawValueParts] = detail.split(':');
        const variantLabel = rawLabel?.trim();
        const variantValue = rawValueParts.join(':').trim();

        if (!variantLabel || !variantValue) {
          return '';
        }

        return `<p class="product-option">${this.#escapeHtml(variantValue)}</p>`;
      })
      .join('');

    const imageMarkup = state.image
      ? `
        <div class="cart-notification-product__image">
          ${productUrl ? `<a href="${productUrl}" class="cart-notification-product__media-link">` : ''}
            <img
              src="${this.#escapeHtml(state.image)}"
              alt="${this.#escapeHtml(state.imageAlt)}"
              loading="lazy"
              width="72"
              height="72"
            >
          ${productUrl ? '</a>' : ''}
        </div>
      `
      : '';

    const countMarkup = state.itemQuantity;

    const titleMarkup = productUrl
      ? `<a href="${productUrl}" class="cart-notification-product__name">${productTitle}</a>`
      : `<p class="cart-notification-product__name">${productTitle}</p>`;

    const detailsSection = detailMarkup
      ? `<div class="cart-notification-product__details-list">${detailMarkup}</div>`
      : '';

    const countSection = countMarkup
    ? `${countMarkup}x`
    : '';

    return `
      <div class="cart-notification-product__card">
        ${imageMarkup}
        <div class="cart-notification-product__content">
          ${titleMarkup}
          ${priceMarkup}
          ${detailsSection}
          ${countSection}
        </div>
      </div>
    `;
  }

  /**
   * @param {string} message
   * @returns {string}
   */
  #renderErrorMessage(message) {
    if (!message) return '';

    return `
      <div class="cart-notification-message cart-notification-message--error" role="alert">
        <p class="cart-notification-message__text">${this.#escapeHtml(message)}</p>
      </div>
    `;
  }

  /**
   * @param {Record<string, unknown>} item
   * @param {number} quantityAdded
   * @returns {string}
   */
  #formatPrice(item, quantityAdded) {
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    const addedQuantity = quantityAdded > 0 ? quantityAdded : 1;
    const rawUnitPrice = item.final_price ?? item.price ?? item.original_price;
    const normalizedUnitPrice = Number(rawUnitPrice);

    if (Number.isFinite(normalizedUnitPrice) && normalizedUnitPrice >= 0) {
      return formatMoney(normalizedUnitPrice * addedQuantity, moneyFormat, currency);
    }

    const rawLinePrice = item.final_line_price ?? item.line_price;
    const normalizedLinePrice = Number(rawLinePrice);

    if (!Number.isFinite(normalizedLinePrice) || normalizedLinePrice < 0) return '';

    return formatMoney(normalizedLinePrice, moneyFormat, currency);
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #normalizeText(value) {
    if (typeof value !== 'string') return '';

    return value.trim();
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escapeHtml(value) {
    return value.replace(/[&<>"']/g, (character) => {
      switch (character) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#39;';
        default:
          return character;
      }
    });
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
